var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { Box, Button, FormHelperText, TextField } from '@mui/material';
import { Formik } from 'formik';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import useAuth from '../../../hooks/useAuth';
import useMounted from '../../../hooks/useMounted';
import { track } from '../../../analytics/analytics';
import { eventTypes } from '../../../analytics/constants';
import { TorqcloudApi } from '../../../api/generated/torqcloud/torqcloudApi';
import { useAppConfig } from '../../../hooks/useConfig';
import { currentLocationPath } from '../../../authentication/returnTo';
import { login } from '../../../constants/routing';
const Login = (props) => {
    var _a;
    const { onForgotPassword, returnTo, allowedReturnOrigins } = props, rest = __rest(props, ["onForgotPassword", "returnTo", "allowedReturnOrigins"]);
    const mounted = useMounted();
    const navigate = useNavigate();
    const { authenticationManager, changeLoggedInUser } = useAuth();
    const appConfig = useAppConfig();
    const torqCloudApi = new TorqcloudApi({ baseUrl: appConfig.torqCloudApiUrl });
    // Where the redirect sign-in lands afterwards: returnTo only when it is on an allowed origin. A
    // malformed returnTo is dropped instead of failing the redirect.
    let safeReturnTo;
    if (returnTo) {
        try {
            const target = new URL(returnTo);
            if (allowedReturnOrigins === null || allowedReturnOrigins === void 0 ? void 0 : allowedReturnOrigins.includes(target.origin)) {
                safeReturnTo = returnTo;
            }
        }
        catch (_b) {
            safeReturnTo = undefined;
        }
    }
    const [passwordShown, setPasswordShown] = useState(false);
    // Redirect sign-in. AuthGuard renders this form in place at the URL the user asked for, with
    // no returnTo, and nothing in React state survives the trip through the provider; so without
    // a returnTo, come back to the current location (same-origin only). On the login page itself
    // there is nothing to come back to.
    const startRedirectSignIn = () => {
        var _a;
        const fallback = window.location.pathname === login() ? undefined : currentLocationPath();
        void ((_a = authenticationManager.startSignIn) === null || _a === void 0 ? void 0 : _a.call(authenticationManager, safeReturnTo !== null && safeReturnTo !== void 0 ? safeReturnTo : fallback).catch(err => {
            const errorMessage = err instanceof Error ? err.message : `${err}`;
            track(eventTypes.LOGIN_FAILED, { errorMessage, method: 'redirect' });
            toast.error(errorMessage);
        }));
    };
    // Show/Hide Password Handler
    const handleClickShowPassword = () => {
        setPasswordShown(!passwordShown);
    };
    const redirectToVerification = (email, username, password) => {
        navigate('/verify-email', {
            state: { email, username, password, fromLogin: true },
        });
    };
    const handleSubmit = (values_1, _a) => __awaiter(void 0, [values_1, _a], void 0, function* (values, { setErrors, setStatus, setSubmitting }) {
        const email = values.email.toLowerCase();
        try {
            const user = yield authenticationManager.signIn(email, values.password);
            // If signIn succeeds, user is confirmed and can proceed
            changeLoggedInUser(user);
            track(eventTypes.USER_LOGIN, { email, method: 'password', hasReturnTo: Boolean(returnTo) });
            if (returnTo && (allowedReturnOrigins === null || allowedReturnOrigins === void 0 ? void 0 : allowedReturnOrigins.includes(new URL(returnTo).origin))) {
                window.location.href = returnTo;
            }
            if (mounted.current) {
                setStatus({ success: true });
                setSubmitting(false);
            }
        }
        catch (err) {
            const errorCode = typeof err === 'object' && err != null && 'code' in err ? err.code : null;
            // If the user doesn't exist, we verify against torqcloud-api. Cognito considers the user not existing if we're
            // using alias_attributes = ['email'] and the email is not confirmed; There's no way to get the uuid _except_
            // from our saved data in torqcloud-api (written on registration). (We're still checking against torqcloud-api
            // even when not in uuid mode; it doesn't hurt, and guarantees the user has a profile within Workforce).
            if (errorCode === 'UserNotConfirmedException' || errorCode === 'UserNotFoundException') {
                const response = yield torqCloudApi.v1.v1UsersLookupUsernameRetrieve({ email }).catch(() => null);
                if (response === null || response === void 0 ? void 0 : response.data.username) {
                    redirectToVerification(email, response.data.username, values.password);
                    if (mounted.current) {
                        setStatus({ success: false });
                        setSubmitting(false);
                    }
                    return;
                }
            }
            const errorMessage = err instanceof Error ? err.message : `${err}`;
            track(eventTypes.LOGIN_FAILED, { email, errorCode, errorMessage });
            toast.error(errorMessage);
            if (mounted.current) {
                setStatus({ success: false });
                setErrors({ submit: errorMessage });
                setSubmitting(false);
            }
        }
    });
    return (_jsx("div", Object.assign({}, rest, { children: authenticationManager.supportsPasswordSignIn === false && authenticationManager.startSignIn ? (_jsx(Box, { sx: { mt: 3 }, gap: 3, children: _jsxs(Button, { sx: theme => ({
                    backgroundColor: theme.palette.primary.main,
                    color: 'black',
                    fontWeight: 'bold',
                    padding: '.4rem 2.45rem',
                    '&:hover': {
                        backgroundColor: theme.typography.button.hover,
                    },
                }), size: "large", type: "button", variant: "contained", onClick: startRedirectSignIn, children: ["Continue with ", (_a = authenticationManager.providerLabel) !== null && _a !== void 0 ? _a : 'Single Sign-On'] }) })) : (_jsxs(_Fragment, { children: [_jsx(Formik, { initialValues: {
                        email: '',
                        password: '',
                        submit: '',
                    }, validationSchema: Yup.object().shape({
                        email: Yup.string().email('Must be a valid email').max(255).required('Email is required'),
                        password: Yup.string().max(255).required('Password is required'),
                    }), onSubmit: handleSubmit, children: ({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (_jsxs("form", Object.assign({ noValidate: true, onSubmit: handleSubmit }, rest, { children: [_jsxs("div", { className: "form-inputs", children: [_jsx("div", { className: "form-input", children: _jsxs("label", { children: ["Email address", _jsx(TextField, { error: Boolean(touched.email && errors.email), fullWidth: true, helperText: touched.email && errors.email, margin: "dense", name: "email", onBlur: handleBlur, onChange: handleChange, type: "email", value: values.email, variant: "outlined", size: "small" })] }) }), _jsx("div", { className: "form-input", style: { marginTop: '1rem' }, children: _jsxs("label", { children: ["Password", _jsx(TextField, { error: Boolean(touched.password && errors.password), fullWidth: true, helperText: touched.password && errors.password, margin: "dense", name: "password", onBlur: handleBlur, onChange: handleChange('password'), type: passwordShown ? 'text' : 'password', value: values.password, variant: "outlined", size: "small" })] }) })] }), _jsx(Box, { className: 'max-width', display: "flex", justifyContent: "end", children: _jsx("a", { style: { alignItems: 'right' }, className: "password-toggle", onClick: handleClickShowPassword, children: passwordShown ? 'Hide password' : 'Show password' }) }), errors.submit && (_jsx(Box, { sx: { mt: 3 }, children: _jsx(FormHelperText, { error: true, children: errors.submit }) })), _jsx(Box, { sx: { mt: 3 }, gap: 3, children: _jsx(Button, { sx: theme => ({
                                        backgroundColor: theme.palette.primary.main,
                                        color: 'black',
                                        fontWeight: 'bold',
                                        padding: '.4rem 2.45rem',
                                        '&:hover': {
                                            backgroundColor: theme.typography.button.hover,
                                        },
                                    }), disabled: isSubmitting, size: "large", type: "submit", variant: "contained", children: "Sign In" }) })] }))) }), _jsx(Box, { sx: { mt: 2 }, children: _jsx("a", { className: "forgot-password italic", onClick: () => onForgotPassword(), children: "Forgot password?" }) })] })) })));
};
export default Login;
//# sourceMappingURL=Login.js.map